/**
 * Choreography Spec — data contract for carousel-to-video conversion.
 *
 * Describes how carousel slides are sequenced into a video:
 * per-slide duration, overlay entrance animations, and transitions between slides.
 *
 * The LLM generates this spec from carousel content; the CarouselVideo
 * Remotion composition consumes it to render the video.
 */

// ---------------------------------------------------------------------------
// Animation timing
// ---------------------------------------------------------------------------

/** Easing-based timing using Remotion's interpolate() */
export interface EasingTiming {
  method: 'easing';
  curve: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'cubic' | 'exp' | 'circle';
}

/** Spring-based timing using Remotion's spring() with preset configs */
export interface SpringTiming {
  method: 'spring';
  /** smooth={damping:200}, snappy={damping:20,stiffness:200}, bouncy={damping:8}, heavy={damping:15,stiffness:80,mass:2} */
  config: 'smooth' | 'snappy' | 'bouncy' | 'heavy';
}

/** Cubic bezier timing using Remotion's Easing.bezier() */
export interface BezierTiming {
  method: 'bezier';
  points: [number, number, number, number];
}

export type AnimationTiming = EasingTiming | SpringTiming | BezierTiming;

// ---------------------------------------------------------------------------
// Overlay entrance animations
// ---------------------------------------------------------------------------

export interface OverlayEntrance {
  /** Must match an overlay.id from the carousel slide's MediaItem.metadata.overlays */
  overlayId: string;
  type:
    | 'fadeIn'       // opacity 0→1
    | 'slideIn'      // translate from offscreen → final position
    | 'spring'       // scale 0→1 with spring physics (bounce)
    | 'scaleIn'      // scale 0→1 with easing (no bounce)
    | 'typewriter'   // text appears char by char (text overlays only)
    | 'wipe'         // clip-path reveal
    | 'blur'         // gaussian blur → clear
    | 'none';        // instant, no animation
  /** Delay from slide start before animation begins */
  delaySeconds: number;
  /** Duration of the animation */
  durationSeconds: number;
  /** Direction for slideIn and wipe types */
  direction?: 'left' | 'right' | 'top' | 'bottom';
  /** Timing method — defaults to { method: 'easing', curve: 'easeOut' } */
  timing?: AnimationTiming;
}

// ---------------------------------------------------------------------------
// Slide transitions
// ---------------------------------------------------------------------------

export interface SlideTransition {
  /** Transition presentation type */
  type: 'fade' | 'slide' | 'wipe' | 'flip' | 'clockWipe' | 'none';
  /** Duration of the transition in seconds */
  durationSeconds: number;
  /** Direction for slide and wipe transitions */
  direction?: 'from-left' | 'from-right' | 'from-top' | 'from-bottom';
  /** Timing mode for the transition */
  timing?: 'linear' | 'spring';
}

// ---------------------------------------------------------------------------
// Per-slide choreography
// ---------------------------------------------------------------------------

export interface SlideChoreography {
  /** 0-based index matching the carousel's MediaItem order */
  slideIndex: number;
  /** How long this slide is visible (excluding transition overlap) */
  durationSeconds: number;
  /** Animation config for each overlay on this slide */
  overlayEntrances: OverlayEntrance[];
  /** Transition to the next slide (last slide should use type: 'none') */
  transitionOut: SlideTransition;
}

// ---------------------------------------------------------------------------
// Animation style
// ---------------------------------------------------------------------------

export type AnimationStyle = 'quick-cuts' | 'smooth' | 'cinematic' | 'gentle';

export const ANIMATION_STYLE_CONFIG: Record<AnimationStyle, {
  label: string;
  vibePrompt: string;
  musicMoods: string[];
  musicGenres: string[];
}> = {
  'quick-cuts': {
    label: 'Quick Cuts',
    vibePrompt: 'Punchy & Energetic — fast, dynamic, high variety',
    musicMoods: ['upbeat', 'dynamic', 'excited', 'bright', 'happy'],
    musicGenres: ['Pop', 'Hip-Hop/Rap', 'Dance & Electronic'],
  },
  'smooth': {
    label: 'Smooth',
    vibePrompt: 'Professional & Clean — measured, polished, refined',
    musicMoods: ['serious', 'calm', 'uplifting', 'inspirational'],
    musicGenres: ['Corporate', 'Ambient', 'Classical'],
  },
  'cinematic': {
    label: 'Cinematic',
    vibePrompt: 'Dramatic & Storytelling — slow reveals, emotional impact',
    musicMoods: ['hopeful', 'inspirational', 'uplifting', 'nostalgic'],
    musicGenres: ['Cinematic', 'Classical', 'Jazz & Blues'],
  },
  'gentle': {
    label: 'Gentle',
    vibePrompt: 'Chill & Minimal — soft, relaxed, understated',
    musicMoods: ['chill', 'relaxed', 'peaceful', 'calm'],
    musicGenres: ['Electronic', 'Ambient', 'Alternative/Indie'],
  },
};

// ---------------------------------------------------------------------------
// Top-level spec
// ---------------------------------------------------------------------------

export interface ChoreographySpec {
  /** Frames per second — default 30 */
  fps: number;
  /** Per-slide choreography, ordered by slideIndex */
  slides: SlideChoreography[];
  /** Animation style used to generate this choreography */
  animationStyle?: AnimationStyle;
}

// ---------------------------------------------------------------------------
// Spring config presets (used by the composition to resolve spec values)
// ---------------------------------------------------------------------------

export const SPRING_CONFIGS = {
  smooth: { damping: 200 },
  snappy: { damping: 20, stiffness: 200 },
  bouncy: { damping: 8 },
  heavy: { damping: 15, stiffness: 80, mass: 2 },
} as const;

// ---------------------------------------------------------------------------
// Directed video: scenes staged from the carousel's own elements
// ---------------------------------------------------------------------------
// A model directs the video: which slides are on stage per scene (several
// stacked = one tall canvas the camera travels), when each element appears,
// what gets emphasized, and what stays pinned. Kept beside ChoreographySpec:
// videos made before directed ones keep playing as they were.

export type DirectedEase = 'linear' | 'out' | 'in' | 'inOut' | 'back' | 'snap';

/** An overlay of one carousel slide. */
export interface DirectedElementRef { slide: number; overlay: string }

/** Keyframes in scene seconds; dx/dy are % of the frame, scale around the element's centre. */
export interface DirectedKey { t: number; opacity?: number; dx?: number; dy?: number; scale?: number; rotate?: number; ease?: DirectedEase }

export interface DirectedReveal {
  type: 'cut' | 'fade' | 'pop' | 'rise' | 'drop' | 'slideLeft' | 'slideRight' | 'wipeRight' | 'wipeDown' | 'blur' | 'words' | 'chars';
  at: number;
  duration?: number;
}

/** Text effects apply to the substring `text` (the whole element if omitted). */
export interface DirectedEmphasis {
  type: 'highlight' | 'color' | 'strike' | 'underline' | 'countUp' | 'pulse';
  at: number;
  duration?: number;
  text?: string;
  color?: string;
  from?: number;
}

export interface DirectedExit { type: 'cut' | 'fade' | 'rise' | 'drop' | 'shrink' | 'slideLeft' | 'slideRight'; at: number; duration?: number }

/** A new text element in the carousel's own typography, positioned in % of the frame. */
export interface DirectedAddText { text: string; styleFrom: DirectedElementRef; x: number; y: number; width: number; fontScale?: number; align?: 'left' | 'center' | 'right'; color?: string }

export interface DirectedSceneElement {
  ref?: DirectedElementRef;
  add?: DirectedAddText;
  /** Which stacked slide the element sits on (defaults to its own slide's). */
  cell?: number;
  reveal?: DirectedReveal;
  keys?: DirectedKey[];
  emphasis?: DirectedEmphasis[];
  exit?: DirectedExit;
  z?: number;
}

/** Camera centre in slide units (slide k of a vertical stack spans y = k..k+1); zoom >= 1. */
export interface DirectedCameraKey { t: number; x: number; y: number; zoom: number; ease?: DirectedEase }

export interface DirectedSceneTransition { type: 'cut' | 'fade' | 'slideLeft' | 'slideUp' | 'wipeLeft' | 'wipeUp'; durationSeconds: number }

export interface DirectedScene {
  durationSeconds: number;
  /** Slides on stage; more than one is one canvas the camera travels across. */
  slides: number[];
  stack?: 'vertical' | 'horizontal';
  /** Draw each slide's own background (default true unless the video has a backdrop). */
  background?: boolean;
  /** "slide:overlayId" (or overlayId for every on-stage slide) left out of this scene. */
  hide?: string[];
  /** Staged elements; on-stage overlays not listed are shown, static, from t=0. */
  elements: DirectedSceneElement[];
  camera?: DirectedCameraKey[];
  transitionOut?: DirectedSceneTransition;
}

export interface DirectedSpec {
  kind: 'directed';
  fps: number;
  /** One background behind every scene; only when every slide shares it. */
  backdrop?: { slide: number };
  /** Chrome fixed on screen for the whole video; only what every slide has in the same place. */
  persistent?: DirectedElementRef[];
  scenes: DirectedScene[];
}

/** What a post stores as its video: the original choreography or a directed video. */
export type VideoChoreography = ChoreographySpec | DirectedSpec;

export function isDirectedSpec(spec: VideoChoreography | null | undefined): spec is DirectedSpec {
  return !!spec && (spec as DirectedSpec).kind === 'directed';
}
